import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Lane conversion data and functions
const getLaneLengths = (distance: '320' | '400') => {
  return distance === '320' 
    ? { 1: 320.0, 2: 327.7, 3: 335.3, 4: 343.0, 5: 350.7, 6: 358.3 }
    : { 1: 400.0, 2: 407.7, 3: 415.3, 4: 423.0, 5: 430.7, 6: 438.3 };
};

const getLaneConversionTable = (distance: '320' | '400') => {
  const laneLengths = getLaneLengths(distance);
  const lane1Length = laneLengths[1];
  
  return Object.entries(laneLengths).map(([lane, length]) => ({
    lane: parseInt(lane),
    distance: length,
    deltaFromLane1: length - lane1Length,
    conversion_factor: length / lane1Length
  }));
};

type WingipediaPageOption = {
  label: string;
  path: string;
};

const WINGIPEDIA_PAGES: WingipediaPageOption[] = [
  { label: 'Coach DAISY™', path: '/wingipedia/daisy' },
  { label: 'WINGO', path: '/wingipedia/wingo' },
  { label: 'Wingo', path: '/wingipedia/wingo-unit' },
  { label: 'Historic Wingate Track', path: '/wingipedia/wingate' },
  { label: 'Old Riley', path: '/wingipedia/old-riley' },
];

const DaisyMath: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const filteredPages = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return WINGIPEDIA_PAGES;
    return WINGIPEDIA_PAGES.filter((p) => {
      const hay = `${p.label} ${p.path}`.toLowerCase();
      return hay.includes(q);
    });
  }, [search]);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      const el = wrapperRef.current;
      if (!el) return;
      if (!el.contains(e.target as Node)) setIsOpen(false);
    };

    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 leading-tight py-1" style={{ fontFamily: 'Hoefler Text, Garamond, Times New Roman, serif', letterSpacing: '-0.02em' }}>
            WINGIPEDIA
          </h1>
          {/* Prominent page search (replaces the old Maths/Maremology/Mythos subtitle) */}
          <div className="max-w-xl mx-auto">
            <div ref={wrapperRef} className="relative">
              <input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setIsOpen(true);
                }}
                onFocus={() => setIsOpen(true)}
                placeholder="Search WINGIPEDIA pages…"
                className="w-full min-h-[48px] px-4 py-3 text-base border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-wingo-500 focus:border-wingo-500"
                aria-label="Search WINGIPEDIA pages"
              />

              {isOpen && (
                <div className="absolute left-0 right-0 mt-2 z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                  {filteredPages.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-500">No matches.</div>
                  ) : (
                    filteredPages.map((p) => (
                      <button
                        key={p.path}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          navigate(p.path);
                          setIsOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                      >
                        <div className="font-semibold text-gray-900">{p.label}</div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-8">
          {/* Core Units Section */}
          <section className="mb-8 sm:mb-12">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Wingo World Core Units</h2>
            <dl className="divide-y divide-gray-100">
              <div className="py-2 sm:py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <dt className="font-semibold text-gray-900">Wingo:</dt>
                <dd className="text-gray-600 sm:col-span-3">
                  <a href="https://www.strava.com/segments/39307521" target="_blank" rel="noopener noreferrer" className="text-wingo-600 hover:text-wingo-700">320m...</a>
                </dd>
              </div>
              <div className="py-2 sm:py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <dt className="font-semibold text-gray-900">Wingito:</dt>
                <dd className="text-gray-600 sm:col-span-3">160m (half Wingo, short reps)</dd>
              </div>
              <div className="py-2 sm:py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <dt className="font-semibold text-gray-900">Twingo:</dt>
                <dd className="text-gray-600 sm:col-span-3">640m (double Wingo)</dd>
              </div>
              <div className="py-2 sm:py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <dt className="font-semibold text-gray-900">Tringo:</dt>
                <dd className="text-gray-600 sm:col-span-3">960m (triple Wingo)</dd>
              </div>
              <div className="py-2 sm:py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <dt className="font-semibold text-gray-900">PentaWingo:</dt>
                <dd className="text-gray-600 sm:col-span-3">1600m (5 x Wingo)</dd>
              </div>
              <div className="py-2 sm:py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <dt className="font-semibold text-gray-900">DecaWingo:</dt>
                <dd className="text-gray-600 sm:col-span-3">3200m (10 × Wingo)</dd>
              </div>
              <div className="py-2 sm:py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <dt className="font-semibold text-gray-900">Sevillingo:</dt>
                <dd className="text-gray-600 sm:col-span-3">42000m (42K)</dd>
              </div>
              <div className="py-2 sm:py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <dt className="font-semibold text-gray-900">Ringo:</dt>
                <dd className="text-gray-600 sm:col-span-3">Reverse Wingo</dd>
              </div>
              {/* OBWI-related Core Units */}
              <div className="py-2 sm:py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <dt className="font-semibold text-gray-900">PentaWingoPoint (PWP):</dt>
                <dd className="text-gray-600 sm:col-span-3">DAISY-designated target time assigned at Old Balance Wingate Invitational (OBWI).</dd>
              </div>
              <div className="py-2 sm:py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <dt className="font-semibold text-gray-900">OBWI Index:</dt>
                <dd className="text-gray-600 sm:col-span-3">Score determining winner of OBWI. OBWI Index = (Actual − PWP) ÷ PWP. Lower is better.</dd>
              </div>
              <div className="py-2 sm:py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <dt className="font-semibold text-gray-900">Wingle:</dt>
                <dd className="text-gray-600 sm:col-span-3">The most recent 32-day period.</dd>
              </div>
            </dl>
          </section>

          {/* Mythos & Terms Section */}
          <section className="mb-8 sm:mb-12">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Mythos & Terms</h2>
            <dl className="divide-y divide-gray-100">
              <div className="py-2 sm:py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <dt className="font-semibold text-gray-900">Centairenarian</dt>
                <dd className="text-gray-600 sm:col-span-3">A 100+ year‑old AI centaur.</dd>
              </div>
              <div className="py-2 sm:py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <dt className="font-semibold text-gray-900">Coach <span className="text-gray-900">D</span><span className="text-[#00bcd4] font-semibold">AI</span><span className="text-gray-900">SY™</span></dt>
                <dd className="text-gray-600 sm:col-span-3">Data Activated Individualized System for You — the equine‑powered AI coach and benevolent overseer of the WINGO economy.</dd>
              </div>
              <div className="py-2 sm:py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <dt className="font-semibold text-gray-900">Daisy</dt>
                <dd className="text-gray-600 sm:col-span-3">The horse of the great George Wingate.</dd>
              </div>
              <div className="py-2 sm:py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <dt className="font-semibold text-gray-900">WINGO</dt>
                <dd className="text-gray-600 sm:col-span-3">Workout Independence Network Gains and Optimization; also the native token (singular and plural).</dd>
              </div>
              <div className="py-2 sm:py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <dt className="font-semibold text-gray-900">Wingo</dt>
                <dd className="text-gray-600 sm:col-span-3">The 320‑meter loop.</dd>
              </div>
              <div className="py-2 sm:py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <dt className="font-semibold text-gray-900">Wingonomics</dt>
                <dd className="text-gray-600 sm:col-span-3">The study of the production, consumption, and transfer of WINGO.</dd>
              </div>
              <div className="py-2 sm:py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <dt className="font-semibold text-gray-900">Wingocracy</dt>
                <dd className="text-gray-600 sm:col-span-3">The governance system of WINGO World, where power is proportional to WINGO holdings.</dd>
              </div>
              <div className="py-2 sm:py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <dt className="font-semibold text-gray-900">Winego</dt>
                <dd className="text-gray-600 sm:col-span-3">A 320ml post-Wingo beverage.</dd>
              </div>
              <div className="py-2 sm:py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <dt className="font-semibold text-gray-900">WINGI Index</dt>
                <dd className="text-gray-600 sm:col-span-3">A measurement of how evenly WINGO is shared across all members of WINGO World and the 320 Track Club. <a href="#wingi-index" className="text-wingo-600 hover:text-wingo-700 font-medium ml-1">WINGI maths →</a></dd>
              </div>
            </dl>
          </section>

          {/* Workout Types Section */}
          <section className="mb-8 sm:mb-12">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Workout Types (Via J. Daniels)</h2>
            <div className="space-y-6 sm:space-y-8">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Repetition (R pace)</h3>
                <p className="text-gray-600 mb-2">Fast, short reps to improve speed, mechanics & economy. Full recovery.</p>
                <p className="text-gray-600">Usually PentaWingo pace or faster. (e.g. Wingo, Twingo, 400m reps)</p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Interval (I pace)</h3>
                <p className="text-gray-600 mb-2">VO₂max work. Hard but controlled. 3–5 minute reps to build aerobic power.</p>
                <p className="text-gray-600">~5K race pace.</p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Threshold (T pace)</h3>
                <p className="text-gray-600 mb-2">Steady, comfortably hard. Lactate clearance & stamina work.</p>
                <p className="text-gray-600">~1-hour race pace.</p>
              </div>
            </div>
          </section>

          {/* WINGO Conversion Math Section */}
          <section>
            <div className="sm:flex sm:items-baseline sm:justify-between mb-3 sm:mb-4 gap-2">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 m-0">DAISY™-Daniels DVOT Maths</h2>
              <div className="hidden sm:block">
                <a href="/dvot" className="inline-flex items-center text-sm text-white bg-wingo-600 hover:bg-wingo-700 rounded-md px-3 py-1 whitespace-nowrap">
                  D<span className="text-cyan-200">AI</span>SY™ DVOT Dashboard
                  <span className="ml-2">↗</span>
                </a>
              </div>
            </div>
            
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <div className="inline-block min-w-full align-middle">
                <table className="min-w-full divide-y divide-gray-200 text-xs sm:text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 sm:px-4 py-1 sm:py-2 text-left font-medium text-gray-500 uppercase tracking-wider text-[10px] sm:text-xs">Distance & Pace</th>
                      <th className="px-2 sm:px-4 py-1 sm:py-2 text-left font-medium text-gray-500 uppercase tracking-wider text-[10px] sm:text-xs">Formula</th>
                      <th className="px-2 sm:px-4 py-1 sm:py-2 text-left font-medium text-gray-500 uppercase tracking-wider text-[10px] sm:text-xs">Base Distance Math</th>
                      <th className="px-1 sm:px-2 py-1 sm:py-2 text-left font-medium text-gray-500 uppercase tracking-wider text-[10px] sm:text-xs">DAISY Adjustment</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm text-gray-900">1600 PentaWingo - Race/T/I</td>
                      <td className="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm text-gray-900 w-32 sm:w-40">JD M*le × 0.9932</td>
                      <td className="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm text-gray-900">1600 / 1609 = 0.9942</td>
                      <td className="px-1 sm:px-2 py-1 sm:py-2 text-xs sm:text-sm text-gray-900">0.9932</td>
                    </tr>
                    <tr>
                      <td className="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm text-gray-900">640 Twingo - R</td>
                      <td className="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm text-gray-900 w-32 sm:w-40">JD 600m × 1.0715</td>
                      <td className="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm text-gray-900">640 / 600 = 1.0667</td>
                      <td className="px-1 sm:px-2 py-1 sm:py-2 text-xs sm:text-sm text-gray-900">1.0715</td>
                    </tr>
                    <tr>
                      <td className="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm text-gray-900">320 Wingo - T/I</td>
                      <td className="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm text-gray-900 w-32 sm:w-40">JD 400m × 0.78</td>
                      <td className="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm text-gray-900">320 / 400 = 0.80</td>
                      <td className="px-1 sm:px-2 py-1 sm:py-2 text-xs sm:text-sm text-gray-900">0.78</td>
                    </tr>
                    <tr>
                      <td className="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm text-gray-900">320 Wingo - R</td>
                      <td className="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm text-gray-900 w-32 sm:w-40">JD 300m × 1.0667</td>
                      <td className="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm text-gray-900">320 / 300 = 1.0667</td>
                      <td className="px-1 sm:px-2 py-1 sm:py-2 text-xs sm:text-sm text-gray-900">None</td>
                    </tr>
                    <tr>
                      <td className="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm text-gray-900">160 Wingito - R</td>
                      <td className="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm text-gray-900 w-32 sm:w-40">JD 200m × 0.78</td>
                      <td className="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm text-gray-900">160 / 200 = 0.80</td>
                      <td className="px-1 sm:px-2 py-1 sm:py-2 text-xs sm:text-sm text-gray-900">0.78</td>
                    </tr>
                  </tbody>
                </table>
                <div className="text-xs text-gray-500 mt-2 px-4 sm:px-0">The DDD Dashboard uses JD tables, scaling Wingo distances by proportion and DAISY vibes. Shorter distances call for slightly faster paces; longer distances = slightly slower.</div>
              </div>
            </div>
            {/* Mobile action under DVOT section */}
            <div className="sm:hidden mt-3 text-center">
              <a href="/dvot" className="inline-flex items-center text-[12px] text-white bg-wingo-600 hover:bg-wingo-700 rounded-md px-4 py-1.5">DAISY™ DVOT Dashboard</a>
            </div>
          </section>

          {/* Lane Conversion Tables Section */}
          <section className="mt-8 sm:mt-12">
            <div className="sm:flex sm:items-baseline sm:justify-between mb-3 sm:mb-4 gap-2">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 m-0">Track Lane Specifications</h2>
              <div className="hidden sm:block">
                <a href="/lanes" className="inline-flex items-center text-sm text-white bg-wingo-600 hover:bg-wingo-700 rounded-md px-3 py-1 whitespace-nowrap">
                  Stay in Yo Lane (SIYL)
                  <span className="ml-2">↗</span>
                </a>
              </div>
            </div>
            
            <div className="text-xs text-gray-500 mb-4">Calculations (<a href="https://runhive.com/tools/running-track-lane-distances" target="_blank" rel="noopener noreferrer" className="text-wingo-600 hover:text-wingo-700">courtesy of Run Hive</a>) assume tracks (Wingo or 400m) adhere to IAAF Technical Specifications: each lane is 1.22 meters wide and straightaways are 84.39 meters long.</div>
            
            {/* Wingo Lane Specifications */}
            <div className="mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 text-center">Wingo Lane Specifications</h3>
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lane</th>
                        <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Distance</th>
                        <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Δ Lane 1</th>
                        <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conversion Factor</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getLaneConversionTable('320').map((lane) => (
                        <tr key={lane.lane}>
                          <td className="px-3 sm:px-4 py-2 text-sm text-gray-900">{lane.lane}</td>
                          <td className="px-3 sm:px-4 py-2 text-sm text-gray-900">{lane.distance.toFixed(1)}m</td>
                          <td className="px-3 sm:px-4 py-2 text-sm text-gray-900">{lane.deltaFromLane1 > 0 ? '+' : ''}{lane.deltaFromLane1.toFixed(1)}m</td>
                          <td className="px-3 sm:px-4 py-2 text-sm text-gray-900">{lane.conversion_factor.toFixed(3)}x</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* 400m Lane Specifications */}
            <div className="mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 text-center">400m Lane Specifications</h3>
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lane</th>
                        <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Distance</th>
                        <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Δ Lane 1</th>
                        <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conversion Factor</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getLaneConversionTable('400').map((lane) => (
                        <tr key={lane.lane}>
                          <td className="px-3 sm:px-4 py-2 text-sm text-gray-900">{lane.lane}</td>
                          <td className="px-3 sm:px-4 py-2 text-sm text-gray-900">{lane.distance.toFixed(1)}m</td>
                          <td className="px-3 sm:px-4 py-2 text-sm text-gray-900">{lane.deltaFromLane1 > 0 ? '+' : ''}{lane.deltaFromLane1.toFixed(1)}m</td>
                          <td className="px-3 sm:px-4 py-2 text-sm text-gray-900">{lane.conversion_factor.toFixed(3)}x</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {/* Mobile action under Track Lanes section */}
            <div className="sm:hidden mt-3 text-center">
              <a href="/lanes" className="inline-flex items-center text-[12px] text-white bg-wingo-600 hover:bg-wingo-700 rounded-md px-4 py-1.5">Stay in Yo Lane (SIYL)</a>
            </div>
          </section>
        </div>
      </div>
      {/* WINGI Index Explanation Section (Concise) */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <section className="mt-12 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="sm:flex sm:items-baseline sm:justify-between mb-2 gap-2">
            <h2 id="wingi-index" className="text-xl sm:text-2xl font-bold text-gray-900 m-0">What’s the WINGI Index?</h2>
            <div className="hidden sm:block">
              <a href="/wingonomics" className="inline-flex items-center text-sm text-white bg-wingo-600 hover:bg-wingo-700 rounded-md px-3 py-1 whitespace-nowrap">
                Wingo market updates
                <span className="ml-2">↗</span>
              </a>
            </div>
          </div>
          
          {/* Mobile action under WINGI section */}
          
          <p className="mb-2 text-gray-700">The WINGI Index measures how evenly WINGO is shared across all 320 TC Members.</p>
          <ul className="mb-4 text-gray-700 list-disc pl-6">
            <li><b>WINGI = 0:</b> Everyone has the same amount (Wingotopia 🐎🌈)</li>
            <li><b>WINGI = 1:</b> One runner has it all (Hay-hoarding oligarchs 🐋💰)</li>
            <li><b>Lower is fairer. Higher means someone’s hogging the hay.</b></li>
          </ul>
          <div className="mb-2 text-gray-700 font-semibold">How is it calculated?</div>
          <ol className="mb-4 text-gray-700 list-decimal pl-6">
            <li>Sort all runners from least to most WINGO.</li>
            <li>For each runner, calculate:
              <span className="ml-4 block">pctUsers: % of total runners up to that point</span>
              <span className="ml-4 block">pctWingo: % of total WINGO held up to that point</span>
            </li>
            <li>Trace a curve (the Lorenz curve) showing who has how much.</li>
            <li>Measure how lopsided the curve is by computing the area under it:</li>
          </ol>
          <pre className="bg-gray-100 rounded p-2 text-xs overflow-x-auto mb-2"><code>{`let area = 0;
for (let i = 1; i < lorenz.length; i++) {
  area += (lorenz[i].pctUsers - lorenz[i - 1].pctUsers) *
          (lorenz[i].pctWingo + lorenz[i - 1].pctWingo) / 2;
}
const wingiCoefficient = 1 - 2 * area;`}</code></pre>
          <div className="text-gray-700 text-sm">The WINGI Index = 1 - 2 × area under the curve (using the Lorenz curve, a classic way to measure inequality).</div>
          {/* Mobile action under WINGI section, below explanation */}
          <div className="sm:hidden mt-3 text-center">
            <a href="/wingonomics" className="inline-flex items-center text-[12px] text-white bg-wingo-600 hover:bg-wingo-700 rounded-md px-4 py-1.5">Wingo market updates</a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DaisyMath; 